import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { BackButton } from "@/components/BackButton";
import { StarField } from "@/components/alignment/StarField";
import { api } from "@/lib/api";
import { CircleCheck, Loader2, XCircle } from "lucide-react";

const MAX_POLLS = 20;
const SPIRITUAL_BG =
  "radial-gradient(1100px 600px at 15% -10%, rgba(37,52,120,0.55), transparent 60%), radial-gradient(1000px 700px at 90% 0%, rgba(88,28,135,0.5), transparent 60%), radial-gradient(900px 900px at 50% 120%, rgba(14,116,144,0.32), transparent 60%), linear-gradient(180deg,#070512 0%,#0A0A14 100%)";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | timeout | error
  const [info, setInfo] = useState(null);
  const polls = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let timer;
    const check = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (data.payment_status === "expired" || data.payment_status === "failed") { setState("error"); return; }
      } catch (e) {
        if (process.env.NODE_ENV !== "production") console.error("Payment status poll failed:", e);
        // transient error — keep polling until MAX_POLLS
      }
      polls.current += 1;
      if (polls.current >= MAX_POLLS) { setState("timeout"); return; }
      timer = setTimeout(check, 2000);
    };
    check();
    return () => clearTimeout(timer);
  }, [sessionId]);

  const accent = state === "paid" ? "#2DD4BF" : state === "polling" ? "#A78BFA" : "#FB7185";

  return (
    <div className="min-h-screen relative text-slate-200 overflow-hidden">
      <div className="absolute inset-0" style={{ background: SPIRITUAL_BG }} />
      <StarField color={accent} />
      <div className="relative z-10">
        <Header />
        <div className="max-w-2xl mx-auto px-6 py-20">
          <BackButton className="mb-6" />
          <div className="rounded-2xl border border-violet-400/25 bg-white/[0.03] backdrop-blur-md p-10 text-center" data-testid="payment-success-card" style={{ boxShadow: `0 0 70px ${accent}22` }}>
            {state === "polling" && (
              <>
                <Loader2 className="w-14 h-14 text-[#A78BFA] animate-spin mx-auto mb-6" />
                <h1 className="font-head font-900 text-2xl text-slate-50 mb-2">Confirming payment…</h1>
                <p className="text-sm text-slate-400">Hang tight while we confirm your card / Apple Pay charge.</p>
              </>
            )}
            {state === "paid" && (
              <>
                <CircleCheck className="w-16 h-16 text-[#2DD4BF] mx-auto mb-6" />
                <h1 className="font-head font-900 text-3xl text-[#2DD4BF] mb-2">Payment confirmed</h1>
                <p className="text-sm text-slate-400 mb-2">Your {info?.tier?.toUpperCase()} subscription is now active{info?.amount_usd ? ` ($${info.amount_usd})` : ""}.</p>
                <Link to="/home" data-testid="success-play-btn" className="mt-6 inline-block rounded-xl px-8 py-3 text-sm font-medium bg-[#2DD4BF] text-slate-900 hover:-translate-y-0.5 transition-transform duration-300">
                  Continue to InnerArcade
                </Link>
              </>
            )}
            {(state === "timeout" || state === "error") && (
              <>
                <XCircle className="w-16 h-16 text-[#FB7185] mx-auto mb-6" />
                <h1 className="font-head font-900 text-2xl text-[#FB7185] mb-2">{state === "timeout" ? "Still processing" : "Payment not confirmed"}</h1>
                <p className="text-sm text-slate-400 mb-6">{state === "timeout" ? "This is taking longer than usual. Check your dashboard shortly — if charged, your plan will activate automatically." : "We couldn't confirm this payment. If you were charged, contact support."}</p>
                <Link to="/dashboard" data-testid="success-dashboard-btn" className="inline-block rounded-xl px-6 py-3 text-sm font-medium border border-white/15 text-slate-200 hover:border-[#A78BFA] transition-colors duration-300">
                  Go to dashboard
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
